import { inject, Injectable } from '@angular/core';
import { MatterportComponent } from '@simlab/matterport';
import { ComponentConfiguration, SpriteConfiguration } from '@simlab/simlab-facility-management/scene-object';
import { TransformationInterface } from '@simOn/common/matterport';
import { MarkerIcons, MarkerStateIconService, TComponentController } from '@simOn/common/scan';
import { EventMarkersService } from '@simOn/event/element/data-access';
import { EventDto } from '@simOn/event/element/models';
import { EventTooltipComponent } from '@simOn/event/element/ui';
import { catchError, concatAll, defer, filter, iif, map, mergeMap, Observable, of, switchMap, take, tap } from 'rxjs';
import { Vector3 } from 'three';
import { ComponentControllerBase } from './component-controller-base';

export interface EventMarkerTag {
  id: string;
  transformation?: TransformationInterface;
  positionDeleted?: boolean;
}

function isFiniteVector(vector?: { x: number; y: number; z: number } | null): boolean {
  return (
    !!vector && Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z)
  );
}

function hasRenderablePosition(tag: EventMarkerTag): boolean {
  return isFiniteVector(tag.transformation?.position) && isFiniteVector(tag.transformation?.normalVector);
}

@Injectable()
export class EventComponentsControllerService extends ComponentControllerBase implements TComponentController {
  private readonly _eventMarkers = inject(EventMarkersService);
  private readonly _markerStates = inject(MarkerStateIconService);
  readonly _nodes: Record<string, MatterportComponent> = {};

  override get mode(): string {
    return 'events';
  }
  public set selectedMode(value: string) {
    value === this.mode ? this._showAll() : this._hideAll();
  }

  readonly components$: Observable<void> = defer(() => this.selectedModel).pipe(
    switchMap(() =>
      this._eventMarkers.events$.pipe(
        tap((events: EventDto[]) => {
          this._removeMissing(events.map((event: EventDto) => event.id));
        }),
        concatAll(),
        filter((event: EventDto) => !!event.transformation?.position),
        map(
          (event: EventDto): EventMarkerTag => ({
            id: event.id,
            transformation: event.transformation ?? undefined,
            positionDeleted: false
          })
        ),
        mergeMap((tag: EventMarkerTag) => {
          return iif(
            () => !!this._nodes[tag.id],
            of(true).pipe(
              tap(() => this.updateState(tag)),
              take(1)
            ),
            of(true).pipe(
              switchMap(() => this.create3DComponent$(tag)),
              take(1),
              catchError((e) => {
                console.error(e);
                return of(e);
              })
            )
          ).pipe(
            take(1),
            catchError((e) => {
              console.error(e);
              return of(e);
            })
          );
        })
      )
    )
  );

  removeDeletedEvent(id: string) {
    this.scanManagerService.component.deleteNote(id);

    if (this._nodes[id]) {
      delete this._nodes[id];
    }
  }

  create3DComponent$(tag: EventMarkerTag) {
    if (!hasRenderablePosition(tag)) {
      return of(null);
    }
    const icons: MarkerIcons = this._markerStates.markerIcons(`/${$localize.locale}/assets/event-marker.svg`);
    const component: ComponentConfiguration = {
      id: tag.id,
      normal: tag.transformation!.normalVector as Vector3,
      stemHeight: tag.transformation!.stemLength || 0,
      position: tag.transformation!.position as Vector3,
      scale: { x: 0.14, y: 0.14, z: 0.14 } as Vector3,
      up: new Vector3(0, 0, 1),
      userData: {
        type: 'events',
        eventId: tag.id
      },
      tooltip: { component: EventTooltipComponent },
      autoScale: false,
      objects: [
        new SpriteConfiguration({
          icon: icons.default,
          selectedHalo: icons.selected
        })
      ]
    };
    return this.runExclusiveMarkerOp$(tag.id, () => {
      this.scanManagerService.component.deleteNote(tag.id);
      return this.scanManagerService.component.addComponentWithOffset$(component).pipe(
        tap((created: MatterportComponent) => {
          this._nodes[tag.id] = created;
        }),
        catchError((e) => {
          console.error(e);
          return of(e);
        })
      );
    });
  }

  updateState(tag: EventMarkerTag): void {
    if (!hasRenderablePosition(tag)) {
      return;
    }
    this.scanManagerService.component.updatePositionWithOffset({
      id: tag.id,
      normal: tag.transformation!.normalVector as Vector3,
      position: tag.transformation!.position as Vector3,
      stemHeight: tag.transformation!.stemLength || 0,
      scale: { x: 0.14, y: 0.14, z: 0.14 } as Vector3
    });
  }

  private _removeMissing(events: string[]) {
    const current = Object.keys(this._nodes);
    if (current.length > 0) {
      const collection = [...current.filter((id) => !events.includes(id))];
      collection.forEach((id) => {
        if (this._nodes[id]) {
          this.scanManagerService.component.deleteNote(id);
          delete this._nodes[id];
        }
      });
    }
  }
}
